import { LayoutMutationAPI } from '../api/layout-mutation-api';
import { PageContext } from '../context/page-context';
import { LayoutProps } from '../props/page/layout-props';
import { LayoutStore } from '../store/layout-store';
import { PageDataModel } from './page-data-model';
import type { IEventManager } from '../manager/event-manager';
import type { ICTPlaceholderPml } from '@tencent/slide-sdk-pb-model';
import type { ILayoutData } from '../data';
import type { ILayoutProps } from '../props/page/layout-props';
import type { RestrictedDataModel } from './data-model';
import type { IMasterDataModel, MasterDataModel } from './master-data-model';
import type { PresentationDataModel } from './presentation-data-model';
import type { ThemeDataModel } from './theme-data-model';
import type { DeepReadonly } from 'ts-essentials';
import type { IShapeDataModel, ShapeDataModel } from './shape-data-model';

export class LayoutDataModel extends PageDataModel<ILayoutData, ILayoutProps> {
    public store: LayoutStore;
    public props: ILayoutProps;
    public api: LayoutMutationAPI;
    public context: PageContext;

    public constructor(data: ILayoutData, events: IEventManager, parent: PresentationDataModel) {
        super(events, parent);

        this.context = new PageContext(data.type);

        const master = parent.store.getMasterById(data.masterId);
        const theme = master?.getTheme();

        if (!master) {
            throw new Error(`missing master: ${data.masterId}`);
        }
        if (!theme) {
            throw new Error('missing theme');
        }

        this.context.setPresentation(this.parent);
        this.context.setLayout(this);
        this.context.setMaster(master as MasterDataModel);
        this.context.setTheme(theme as ThemeDataModel);
        this.context.setPage(this);
        this.context.setTarget(this);

        this.store = new LayoutStore(data, this.events, this.context, this);
        this.props = new LayoutProps(this.store.data, this.context);
        this.api = new LayoutMutationAPI(this, events);
    }

    /**
     * 判断是否为标题占位符，暂时只通过id来识别
     * @returns 标题占位符为true
     */
    public get isTitleLayout() {
        return this.id === '/ppt/slideLayouts/slideLayout1.xml';
    }

    public getLayout(): ILayoutDataModel {
        return this;
    }

    public getMaster(): IMasterDataModel | undefined {
        return this.parent.store.getMasterById(this.store.data.masterId);
    }

    /**
     * 获取layout上全量占位符，此接口用于通过版式窗格创建 slide
     * @param layoutId layout的id
     * @returns
     */
    public getInheritedPlaceholders() {
        return this.placeholders
            .map((shape) => this.store.getModifyPlaceholderData(shape, this))
            .filter((shape) => !!shape);
    }

    public getPlaceholderShape(ph: DeepReadonly<ICTPlaceholderPml>): IShapeDataModel | undefined {
        return this.store.findPlaceholderShape<ShapeDataModel>(ph);
    }

    public forEachDescendant(callback: (child: RestrictedDataModel) => void = () => void 0) {
        if (this.timing) {
            callback(this.timing);
        }
        super.forEachDescendant(callback);
    }
}

export { ILayoutProps };
export type ILayoutDataModel = RestrictedDataModel<LayoutDataModel>;
